/********************************************************************
 * ADOBE CONFIDENTIAL
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { fetchGraphQl } from '@/order/api';
import { OrderDataModel } from '@/order/data/models';
import { transformPlaceOrder } from '@/order/data/transforms';
import { publishPlaceOrderEvent } from '@/order/lib/acdl';
import { handleFetchError } from '@/order/lib/fetch-error';
import { handleNetworkError } from '@/order/lib/network-error';
import { handlePlaceOrderError } from '@/order/lib/place-order-error';
import { PlaceOrderResponse } from '@/order/types/index';
import { events } from '@adobe-commerce/event-bus';
import { PLACE_ORDER_MUTATION } from './graphql/placeOrderMutation';
import { setReCaptchaToken } from '@/order/lib/setReCaptchaToken';

export const placeOrder = async (
  cartId: string
): Promise<OrderDataModel | null | undefined> => {
  if (!cartId) throw new Error('No cart ID found');
  await setReCaptchaToken();

  return fetchGraphQl(PLACE_ORDER_MUTATION, {
    method: 'POST',
    variables: { cartId },
  })
    .then((response: PlaceOrderResponse) => {
      if (response.errors?.length) {
        handleFetchError(response.errors);
      }

      if (response.data?.placeOrder?.errors?.length) {
        handlePlaceOrderError(response.data.placeOrder?.errors);
      }

      const payload = transformPlaceOrder(response);

      if (payload) {
        events.emit('order/placed', payload);
        events.emit('cart/reset', undefined);
        publishPlaceOrderEvent(cartId, payload);
      }

      return payload;
    })
    .catch(handleNetworkError);
};
